import React, {FC, useEffect, useState} from 'react'
import {KTIcon} from '../../../../helpers'
import {useLayout} from '../../../../layout/core'
import moment from 'moment'
import Swal from 'sweetalert2'

type FullTransactionType = {
  id: string
  sender: string
  recipient: string
  currency: string
  amount: number
  fees: number
  status: string
  timestamp: string
  note: string
  type: string
  // Advanced UI Fields
  icon?: string
  network?: string
  tx_hash?: string
  gas_fee?: number
  confirmations?: number
  usd_equivalent?: number
  fee_breakdown?: {
    platform: number
    network: number
    spread: number
  }
  ip_address?: string
  device?: string
  coinme_ref?: string
  ctr_flag?: boolean
  error_reason?: string
  user_email?: string
  payment_method?: {
    id: string
    type: string
    last4: string
    brand: string
  }
  source_wallet?: string
  destination_wallet?: string
  fee?: string | number
}

const DrawerTransactionHistory: FC = () => {
  const {transactionHistoryDetails, setTransactionHistoryDetails} = useLayout()
  const [txnDetails, setTxnDetails] = useState<FullTransactionType | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const handleCopy = (text: string, fieldName: string) => {
    if (!text) return
    const cb = () => {
      setCopiedField(fieldName)
      setTimeout(() => setCopiedField(null), 1500)
      Swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: `${fieldName} copied to clipboard.`,
        timer: 1000,
        showConfirmButton: false,
      })
    }
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(cb).catch(() => fallbackCopy(text, cb))
    } else {
      fallbackCopy(text, cb)
    }
  }

  const fallbackCopy = (text: string, cb: () => void) => {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.top = '0'
    textArea.style.left = '0'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    try {
      document.execCommand('copy')
      cb()
    } catch (err) {
      console.error('Fallback copy failed', err)
    }
    document.body.removeChild(textArea)
  }

  useEffect(() => {
    if (transactionHistoryDetails) {
      setTxnDetails(transactionHistoryDetails)
    }
  }, [transactionHistoryDetails])

  // Sync drawer when transactions list changes its status externally
  useEffect(() => {
    const handleUpdate = (e: any) => {
      const {id, newStatus} = e.detail
      if (txnDetails && txnDetails.id === id) {
        setTxnDetails((prev) => (prev ? {...prev, status: newStatus} : null))
      }
    }
    window.addEventListener('update-transaction', handleUpdate)
    return () => window.removeEventListener('update-transaction', handleUpdate)
  }, [txnDetails])

  const getExplorerLink = (network: string, txHash: string) => {
    if (!txHash || txHash === 'N/A' || txHash.startsWith('CM-')) return null
    const net = (network || '').toLowerCase()
    if (net.includes('bitcoin') || net.includes('btc')) {
      return `https://blockstream.info/tx/${txHash}`
    }
    if (net.includes('ethereum') || net.includes('eth') || net.includes('erc-20')) {
      return `https://etherscan.io/tx/${txHash}`
    }
    if (net.includes('solana') || net.includes('sol')) {
      return `https://solscan.io/tx/${txHash}`
    }
    if (net.includes('polygon') || net.includes('pol') || net.includes('matic')) {
      return `https://polygonscan.com/tx/${txHash}`
    }
    if (net.includes('xrp') || net.includes('ledger')) {
      return `https://xrpscan.com/tx/${txHash}`
    }
    return null
  }

  const getTroubleshootingInfo = (network: string, type: string, errorReason?: string) => {
    const net = (network || '').toLowerCase()
    const t = (type || '').toLowerCase()

    if (net.includes('bitcoin') || net.includes('btc')) {
      return {
        issue: 'Stuck BTC Mempool',
        desc: 'This transaction is currently stuck in the Bitcoin mempool due to low fee density. Admin Action Required: Retry with a higher fee or Cancel.',
      }
    }
    if (
      net.includes('ethereum') ||
      net.includes('eth') ||
      net.includes('polygon') ||
      net.includes('pol')
    ) {
      return {
        issue: 'Underpriced Gas Fee',
        desc: 'This transaction is stuck due to an underpriced Ethereum/Polygon gas fee. Admin Action Required: Speed up (retry with higher fee) or Cancel.',
      }
    }
    if (net.includes('solana') || net.includes('sol')) {
      return {
        issue: 'Failed Solana Transaction',
        desc: 'This Solana transaction failed due to network slippage or temporary RPC node congestion. Admin Action Required: Retry the transaction.',
      }
    }
    if (t.includes('buy') || t.includes('sell')) {
      return {
        issue: 'Coinme On-Ramp Failure',
        desc: 'The card on-ramp authorization failed on the Coinme provider-side. Admin Action Required: Retry or Escalate to Coinme.',
      }
    }
    return {
      issue: 'Transaction Failure',
      desc: errorReason || 'The transaction failed during processing. Admin Action Required: Mark as resolved or Cancel.',
    }
  }

  const triggerAction = (actionName: string, targetStatus: string, successMessage: string) => {
    if (!txnDetails) return

    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to ${actionName} this transaction?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1B84FF',
      cancelButtonColor: '#F1416C',
      confirmButtonText: 'Yes, proceed!',
    }).then((result) => {
      if (result.isConfirmed) {
        // Dispatch global CustomEvent to update AllTransactions page state
        const event = new CustomEvent('update-transaction', {
          detail: {id: txnDetails.id, newStatus: targetStatus},
        })
        window.dispatchEvent(event)

        // Show success alert
        Swal.fire({
          title: 'Success!',
          text: successMessage,
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        })
      }
    })
  }

  const escalateToProvider = () => {
    if (!txnDetails) return

    Swal.fire({
      title: 'Escalate to Coinme?',
      text: 'This will open a support ticket on the Coinme provider dashboard.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#F1BC06',
      cancelButtonColor: '#7E8299',
      confirmButtonText: 'Yes, Escalate',
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Escalated!',
          text: 'Ticket escalated to Coinme support team. Reference: CM-ESC-9821.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        })
      }
    })
  }

  const explorerUrl = txnDetails ? getExplorerLink(txnDetails.network || '', txnDetails.tx_hash || '') : null
  const troubleshooting = txnDetails
    ? getTroubleshootingInfo(txnDetails.network || '', txnDetails.type || '', txnDetails.error_reason)
    : null

  const isTroubleshootable =
    txnDetails &&
    (txnDetails.status === 'failed' ||
      txnDetails.status === 'pending' ||
      txnDetails.status === 'created' ||
      txnDetails.status === 'cancelled' ||
      txnDetails.status === 'held')

  return (
    <div
      id='transaction-history-drawer'
      className='bg-body'
      data-kt-drawer='true'
      data-kt-drawer-name='transaction-history'
      data-kt-drawer-activate='true'
      data-kt-drawer-overlay='true'
      data-kt-drawer-width="{default:'300px', 'lg': '600px'}"
      data-kt-drawer-direction='end'
      data-kt-drawer-toggle='#transaction-history-drawer-toggle'
      data-kt-drawer-close='#transaction-history-close'
    >
      <div className='card rounded-0 w-100 h-100 shadow-lg border-0'>
        {/* Header */}
        <div className='card-header border-0 pt-5 pb-3'>
          <h3 className='card-title fw-bold text-gray-900 fs-3'>Transaction Details</h3>
          <div className='card-toolbar'>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light border border-gray-200'
              id='transaction-history-close'
              onClick={() => setTransactionHistoryDetails('')}
            >
              <KTIcon iconName='cross' className='fs-2 text-gray-700' />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className='card-body bg-light-soft scroll-y pt-0 px-9 pb-10'>
          {!txnDetails ? (
            <div className='text-center py-20'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            <div className='d-flex flex-column gap-6 mt-3'>
              {/* CTR Alert */}
              {txnDetails.ctr_flag && (
                <div className='alert alert-danger d-flex align-items-center p-4 rounded-3 border-danger mb-0'>
                  <KTIcon iconName='information-5' className='fs-1 text-danger me-3' />
                  <div className='d-flex flex-column'>
                    <span className='fw-bold fs-6 text-danger'>CTR Threshold Reached</span>
                    <span className='text-gray-700 fs-7'>
                      This transaction exceeds $10,000 USD and meets the Currency Transaction Report threshold.
                    </span>
                  </div>
                </div>
              )}

              {/* Status Header Card */}
              <div className='d-flex align-items-center justify-content-between p-5 bg-body rounded-3 shadow-sm border border-gray-200'>
                <div className='d-flex align-items-center gap-3'>
                  <div className='symbol symbol-45px symbol-circle bg-light d-flex align-items-center justify-content-center p-2'>
                    {txnDetails.icon ? (
                      <img
                        src={txnDetails.icon}
                        alt='asset-icon'
                        className='w-30px h-30px object-fit-contain'
                      />
                    ) : (
                      <div className='w-30px h-30px bg-primary rounded-circle d-flex align-items-center justify-content-center text-white fw-bold'>
                        {txnDetails.currency.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className='d-flex flex-column'>
                    <span className='fw-bolder fs-4 text-gray-900'>
                      {txnDetails.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 8,
                      })}{' '}
                      {txnDetails.currency}
                    </span>
                    <span className='text-success fw-bold fs-7'>
                      ${txnDetails.usd_equivalent?.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }) || '0.00'}{' '}
                      USD
                    </span>
                  </div>
                </div>
                <span
                  className={`badge px-3 py-2 fs-7 fw-bold text-capitalize ${
                    txnDetails.status === 'success' ||
                    txnDetails.status === 'complete' ||
                    txnDetails.status === 'completed'
                      ? 'badge-light-success'
                      : txnDetails.status === 'pending' ||
                        txnDetails.status === 'created' ||
                        txnDetails.status === 'processing'
                      ? 'badge-light-warning'
                      : txnDetails.status === 'failed' || txnDetails.status === 'cancelled'
                      ? 'badge-light-danger'
                      : 'badge-light-info'
                  }`}
                >
                  {txnDetails.status}
                </span>
              </div>

              {/* Core Blockchain / Network Data */}
              <div className='card shadow-xs border border-gray-200 rounded-3'>
                <div className='card-header min-h-40px bg-light-primary border-bottom-0 py-3 rounded-top-3'>
                  <h3 className='card-title fs-6 fw-bold text-primary m-0'>Core Network Data</h3>
                </div>
                <div className='card-body p-5 d-flex flex-column gap-3'>
                  <div className='d-flex justify-content-between'>
                    <span className='fw-semibold text-gray-500 fs-7'>Network / Chain:</span>
                    <span className='fw-bold text-gray-800 fs-7'>{txnDetails.network || '-'}</span>
                  </div>
                  <div className='d-flex justify-content-between align-items-center'>
                    <span className='fw-semibold text-gray-500 fs-7'>Tx Hash / Ref:</span>
                    <div className='d-flex align-items-center gap-2'>
                      {explorerUrl ? (
                        <a
                          href={explorerUrl}
                          target='_blank'
                          rel='noreferrer'
                          className='fw-bold text-primary fs-7 text-truncate d-flex align-items-center gap-1 ms-4'
                          style={{maxWidth: '180px'}}
                        >
                          {txnDetails.tx_hash}
                          <i className='bi bi-box-arrow-up-right fs-9'></i>
                        </a>
                      ) : (
                        <span className='fw-bold text-gray-800 fs-7 text-truncate ms-4' style={{maxWidth: '180px'}}>
                          {txnDetails.tx_hash || txnDetails.id}
                        </span>
                      )}
                      <button
                        className='btn btn-icon btn-xs btn-light-primary border-0 p-0 d-inline-flex align-items-center justify-content-center ms-1'
                        onClick={() => handleCopy(txnDetails.tx_hash || txnDetails.id || '', 'Tx Hash')}
                        title='Copy Tx Hash'
                        style={{ width: '22px', height: '22px', minWidth: '22px' }}
                      >
                        {copiedField === 'Tx Hash' ? (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#50CD89" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#009EF7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  {txnDetails.source_wallet && (
                    <div className='d-flex justify-content-between align-items-center'>
                      <span className='fw-semibold text-gray-500 fs-7'>Source Wallet:</span>
                      <div className='d-flex align-items-center gap-2'>
                        <span className='fw-bold text-gray-800 fs-7 text-truncate ms-4 font-monospace' style={{maxWidth: '180px'}} title={txnDetails.source_wallet}>
                          {txnDetails.source_wallet}
                        </span>
                        <button
                          className='btn btn-icon btn-xs btn-light-primary border-0 p-0 d-inline-flex align-items-center justify-content-center ms-1'
                          onClick={() => handleCopy(txnDetails.source_wallet || '', 'Source Wallet')}
                          title='Copy Source Wallet'
                          style={{ width: '22px', height: '22px', minWidth: '22px' }}
                        >
                          {copiedField === 'Source Wallet' ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#50CD89" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#009EF7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  {txnDetails.destination_wallet && (
                    <div className='d-flex justify-content-between align-items-center'>
                      <span className='fw-semibold text-gray-500 fs-7'>Destination Wallet:</span>
                      <div className='d-flex align-items-center gap-2'>
                        <span className='fw-bold text-gray-800 fs-7 text-truncate ms-4 font-monospace' style={{maxWidth: '180px'}} title={txnDetails.destination_wallet}>
                          {txnDetails.destination_wallet}
                        </span>
                        <button
                          className='btn btn-icon btn-xs btn-light-primary border-0 p-0 d-inline-flex align-items-center justify-content-center ms-1'
                          onClick={() => handleCopy(txnDetails.destination_wallet || '', 'Destination Wallet')}
                          title='Copy Destination Wallet'
                          style={{ width: '22px', height: '22px', minWidth: '22px' }}
                        >
                          {copiedField === 'Destination Wallet' ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#50CD89" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#009EF7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  {txnDetails.fee !== undefined && txnDetails.fee !== null && (
                    <div className='d-flex justify-content-between align-items-center'>
                      <span className='fw-semibold text-gray-500 fs-7'>Fee:</span>
                      <span className='fw-bold text-gray-800 fs-7 me-5'>
                        ${Number(txnDetails.fee).toFixed(2)} USD
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Method Details */}
              <div className='card shadow-xs border border-gray-200 rounded-3'>
                <div className='card-header min-h-40px bg-light-success border-bottom-0 py-3 rounded-top-3'>
                  <h3 className='card-title fs-6 fw-bold text-success m-0'>Payment Method Details</h3>
                </div>
                <div className='card-body p-5 d-flex flex-column gap-3'>
                  {txnDetails.payment_method ? (
                    <>
                      <div className='d-flex justify-content-between align-items-center'>
                        <span className='fw-semibold text-gray-500 fs-7'>Type:</span>
                        <span className='fw-bold text-gray-800 fs-7 text-capitalize'>
                          {txnDetails.payment_method.type?.toLowerCase()} Card
                        </span>
                      </div>
                      <div className='d-flex justify-content-between align-items-center'>
                        <span className='fw-semibold text-gray-500 fs-7'>Brand:</span>
                        <span className='fw-bold text-gray-800 fs-7'>
                          {txnDetails.payment_method.brand || 'Card'}
                        </span>
                      </div>
                      <div className='d-flex justify-content-between align-items-center'>
                        <span className='fw-semibold text-gray-500 fs-7'>Card Number:</span>
                        <span className='fw-bold text-gray-800 fs-7 font-monospace'>
                          •••• •••• •••• {txnDetails.payment_method.last4 || '••••'}
                        </span>
                      </div>
                      <div className='d-flex justify-content-between align-items-center'>
                        <span className='fw-semibold text-gray-500 fs-7'>Payment ID:</span>
                        <span className='fw-bold text-gray-500 fs-8 font-monospace'>
                          {txnDetails.payment_method.id}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className='d-flex justify-content-between align-items-center'>
                      <span className='fw-semibold text-gray-500 fs-7'>Source/Destination:</span>
                      <span className='fw-bold text-gray-800 fs-7'>Crypto Wallet (On-Chain)</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Compliance & Identity */}
              <div className='card shadow-xs border border-gray-200 rounded-3'>
                <div className='card-header min-h-40px bg-light-info border-bottom-0 py-3 rounded-top-3'>
                  <h3 className='card-title fs-6 fw-bold text-info m-0'>Compliance & Device Details</h3>
                </div>
                <div className='card-body p-5 d-flex flex-column gap-3'>
                  <div className='d-flex justify-content-between'>
                    <span className='fw-semibold text-gray-500 fs-7'>Coinme On-ramp Ref ID:</span>
                    <span className='fw-bold text-gray-800 fs-7'>{txnDetails.coinme_ref || 'N/A'}</span>
                  </div>
                  <div className='d-flex justify-content-between'>
                    <span className='fw-semibold text-gray-500 fs-7'>Linked User (Sender):</span>
                    <div className='d-flex flex-column align-items-end'>
                      <span className='fw-bold text-gray-800 fs-7'>{txnDetails.sender}</span>
                      <span className='text-muted fs-8'>{txnDetails.user_email || 'N/A'}</span>
                    </div>
                  </div>
                  <div className='d-flex justify-content-between'>
                    <span className='fw-semibold text-gray-500 fs-7'>Transaction Time:</span>
                    <span className='fw-bold text-gray-800 fs-7'>
                      {moment(txnDetails.timestamp).format('DD MMM YYYY, hh:mm A')}
                    </span>
                  </div>
                  <div className='d-flex justify-content-between'>
                    <span className='fw-semibold text-gray-500 fs-7'>Category / Purpose:</span>
                    <span className='fw-bold text-gray-800 fs-7 text-capitalize'>
                      {txnDetails.note || txnDetails.type?.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Troubleshooting & Admin Handling Actions */}
              {isTroubleshootable && troubleshooting && (
                <div className='card border border-danger shadow-sm rounded-3'>
                  <div className='card-header min-h-40px bg-light-danger border-bottom-0 py-3 rounded-top-3'>
                    <h3 className='card-title fs-6 fw-bold text-danger m-0'>
                      Failed / Stuck Transaction Handling
                    </h3>
                  </div>
                  <div className='card-body p-5 d-flex flex-column gap-4'>
                    <div className='p-4 bg-light-danger rounded-3 border border-dashed border-danger'>
                      <span className='fw-bolder text-danger fs-6 d-block mb-1'>
                        {troubleshooting.issue}
                      </span>
                      <span className='text-gray-800 fs-7'>{troubleshooting.desc}</span>
                    </div>

                    <div className='row g-2'>
                      <div className='col-6'>
                        <button
                          className='btn btn-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-2'
                          onClick={() =>
                            triggerAction(
                              'Retry',
                              'completed',
                              'Transaction has been successfully retried and broadcasted to the blockchain network.'
                            )
                          }
                        >
                          <i className='bi bi-arrow-repeat fs-6'></i>
                          Retry TX
                        </button>
                      </div>
                      <div className='col-6'>
                        <button
                          className='btn btn-danger btn-sm w-100 d-flex align-items-center justify-content-center gap-2'
                          onClick={() =>
                            triggerAction(
                              'Cancel',
                              'cancelled',
                              'Transaction cancelled and client funds returned.'
                            )
                          }
                        >
                          <i className='bi bi-trash fs-6'></i>
                          Cancel TX
                        </button>
                      </div>
                      <div className='col-12'>
                        <button
                          className='btn btn-warning btn-sm w-100 d-flex align-items-center justify-content-center gap-2'
                          onClick={escalateToProvider}
                        >
                          <i className='bi bi-envelope-exclamation fs-6'></i>
                          Escalate to Coinme
                        </button>
                      </div>
                      <div className='col-12'>
                        <button
                          className='btn btn-light-success btn-sm w-100 border border-success d-flex align-items-center justify-content-center gap-2 mt-1'
                          onClick={() =>
                            triggerAction(
                              'Mark as Resolved',
                              'completed',
                              'Transaction has been manually marked as Completed.'
                            )
                          }
                        >
                          <i className='bi bi-check-circle fs-6'></i>
                          Mark as Resolved
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export {DrawerTransactionHistory}
